import { lazy } from "react";
import withLightHeaderAndFooter from "../HOC/withLightHeaderAndFooter";
import withRegistrationHeader from "../HOC/withRegistrationHeader";
import withDashboardMenu from "../HOC/withDashboardMenu";
import { serviceCreationRoutes } from "../Pages/AiServiceCreation/Routes";
import { organizationSetupRoutes } from "../Pages/OrganizationSetup/Routes";
import { onboardingRoutes } from "../Pages/Onboarding/Routes";

const Enroll = lazy(() => import("../Pages/Enroll"));
const Login = lazy(() => import("../Pages/Login"));
const Signup = lazy(() => import("../Pages/Signup"));
const ForgotPassword = lazy(() => import("../Pages/ForgotPassword"));
const ForgotPasswordConfirm = lazy(() => import("../Pages/ForgotPasswordConfirm"));
const Overview = lazy(() => import("../Pages/Overview"));
const HowItWorks = lazy(() => import("../Pages/HowItWorks"));
const SignupConfirm = lazy(() => import("../Pages/SignupConfirm"));
const Onboarding = lazy(() => import("../Pages/Onboarding"));
const OrganizationSetup = lazy(() => import("../Pages/OrganizationSetup"));
const OrgSetupStatus = lazy(() => import("../Pages/OrgSetupStatus"));
const TeamMembers = lazy(() => import("../Pages/TeamMembers"));
const AiServices = lazy(() => import("../Pages/AiServices"));
const AiServiceCreation = lazy(() => import("../Pages/AiServiceCreation"));
const UserProfile = lazy(() => import("../Pages/UserProfile"));
const FAQ = lazy(() => import("../Pages/FAQ"));

const MyDeployments = lazy(() => import("../Components/HaaS/Components/MyDeployments"));
const BalanceHistory = lazy(
    () => import("../Components/HaaS/Components/MyDeployments/BalanceHistory")
);
const DaemonInfo = lazy(() => import("../Components/HaaS/Components/MyDeployments/DaemonInfo"));
const ServiceInfo = lazy(() => import("../Components/HaaS/Components/MyDeployments/ServiceInfo"));

const SIGNUP_PATH = "/signup";
const LOGIN_PATH = "/login";

const EnrollComponent = withLightHeaderAndFooter(Enroll);
const LoginComponent = withRegistrationHeader(
    Login,
    "New to SingularityNET?",
    "Sign up",
    SIGNUP_PATH
);
const SignupComponent = withRegistrationHeader(
    Signup,
    "Already have an account?",
    "Login",
    LOGIN_PATH
);
const SingupConfirmComponent = withRegistrationHeader(SignupConfirm);
const ForgotPasswordComponent = withRegistrationHeader(
    ForgotPassword,
    "Switch to another account?",
    "Login",
    LOGIN_PATH
);
const ForgotPasswordConfirmComponent = withRegistrationHeader(
    ForgotPasswordConfirm,
    "Switch to another account?",
    "Login",
    LOGIN_PATH
);
export const OverviewComponent = withLightHeaderAndFooter(Overview);
const HowItWorksComponent = withLightHeaderAndFooter(HowItWorks);
const OnboardingComponent = withLightHeaderAndFooter(Onboarding);
const OrganizationsetupComponent = withLightHeaderAndFooter(OrganizationSetup);
const OrgSetupStatusComponent = withLightHeaderAndFooter(OrgSetupStatus);
const TeamMembersComponent = withDashboardMenu(TeamMembers);
const AiServicesComponent = withDashboardMenu(AiServices);
const AiServiceCreationComponent = withLightHeaderAndFooter(AiServiceCreation);
const UserProfileComponent = withDashboardMenu(UserProfile);
const FAQComponent = withDashboardMenu(FAQ);
const MyDeploymentsComponent = withDashboardMenu(MyDeployments);
const BalanceHistoryComponent = withDashboardMenu(BalanceHistory);
const DaemonInfoComponent = withDashboardMenu(DaemonInfo);
const ServiceInfoComponent = withDashboardMenu(ServiceInfo);

export const PUBLIC_PATHS = [
    "/login",
    "/signup",
    "/signupconfirmation",
    "/forgotpassword",
    "/forgotpasswordconfirm",
    "/resetpassword",
    "/howitworks",
    "/faq",
    "/enroll",
];

export const publicRoutes = {
    LOGIN: {
        name: "login",
        path: "/login",
        element: <LoginComponent />,
        isPublic: true,
        meta: {
            title: "Sign In",
            hideInMenu: true,
        },
    },

    SIGNUP: {
        name: "signup",
        path: "/signup",
        element: <SignupComponent />,
        isPublic: true,
        meta: {
            title: "Create Account",
            hideInMenu: true,
        },
    },

    SIGNUP_CONFIRM: {
        name: "signup confirm",
        path: "/signupconfirmation",
        element: <SingupConfirmComponent />,
        isPublic: true,
        meta: {
            title: "Confirm Sign Up",
            hideInMenu: true,
        },
    },

    FORGOT_PASSWORD: {
        name: "forgot password",
        path: "/forgotpassword",
        element: <ForgotPasswordComponent />,
        isPublic: true,
        meta: {
            title: "Forgot Password",
            hideInMenu: true,
        },
    },

    FORGOT_PASSWORD_CONFIRM: {
        name: "confirm forgot password",
        path: "/forgotpasswordconfirm",
        element: <ForgotPasswordConfirmComponent />,
        isPublic: true,
        meta: {
            title: "Confirm Password Reset",
            hideInMenu: true,
        },
    },

    RESET_PASSWORD: {
        name: "reset password",
        path: "/resetpassword",
        element: <ForgotPasswordComponent />,
        isPublic: true,
        meta: {
            title: "Reset Password",
            hideInMenu: true,
        },
    },

    HOW_IT_WORKS: {
        name: "how it works",
        path: "/howitworks",
        element: <HowItWorksComponent />,
        isPublic: true,
        meta: {
            title: "How It Works",
            hideInMenu: false,
        },
    },

    FAQ: {
        name: "FAQ",
        path: "/faq",
        element: <FAQComponent />,
        isPublic: true,
        meta: {
            title: "Frequently Asked Questions",
            hideInMenu: false,
        },
    },

    ENROLL: {
        name: "enroll",
        path: "/enroll",
        element: <EnrollComponent />,
        isPublic: true,
        meta: {
            title: "Enroll",
            hideInMenu: false,
        },
    },

    OVERVIEW: {
        name: "overview",
        path: "/overview",
        element: <OverviewComponent />,
        isPublic: true,
        meta: {
            title: "Dashboard",
            icon: "dashboard",
        },
    },
};

export const protectedRoutes = {
    ONBOARDING: {
        name: "onboarding",
        path: "/onboarding",
        element: <OnboardingComponent />,
        isPublic: false,
        children: Object.values(onboardingRoutes),
        meta: {
            title: "Onboarding",
            hideInMenu: true,
        },
    },

    ORGANIZATION_SETUP: {
        name: "organizationsetup",
        path: "/org/:orgUuid/setup",
        element: <OrganizationsetupComponent />,
        isPublic: false,
        children: Object.values(organizationSetupRoutes),
        meta: {
            title: "Organization Setup",
        },
    },

    ORGANIZATION_STATUS: {
        name: "organization setup",
        path: "/org/:orgUuid/status",
        element: <OrgSetupStatusComponent />,
        isPublic: false,
        meta: {
            title: "Setup Status",
        },
    },

    BALANCE_HISTORY: {
        name: "balance history",
        path: "/deployments/balance-history",
        element: <BalanceHistoryComponent />,
        isPublic: false,
    },

    INVITE_MEMBERS: {
        name: "team members",
        path: "/org/:orgUuid/invitemembers",
        element: <TeamMembersComponent />,
        isPublic: false,
        meta: {
            title: "Invite Team Members",
        },
    },

    SERVICES: {
        name: "services",
        path: "/org/:orgUuid/services",
        element: <AiServicesComponent />,
        isPublic: false,
        meta: {
            title: "AI Services",
        },
    },

    AI_SERVICE_CREATION: {
        name: "ai service creation",
        path: "/org/:orgUuid/service/:serviceUuid/create",
        element: <AiServiceCreationComponent />,
        isPublic: false,
        children: Object.values(serviceCreationRoutes),
        meta: {
            title: "Create AI Service",
            hideInMenu: true,
        },
    },

    MY_DEPLOYMENTS: {
        name: "my deployments",
        path: "/deployments",
        element: <MyDeploymentsComponent />,
        isPublic: false,
        meta: {
            title: "My Deployments",
        },
    },

    DAEMON_INFO: {
        name: "daemon info",
        path: "/daemon/:daemonId",
        element: <DaemonInfoComponent />,
        isPublic: false,
        meta: {
            title: "Daemon Information",
        },
    },

    SERVICE_INFO: {
        name: "service info",
        path: "/service/:serviceId",
        element: <ServiceInfoComponent />,
        isPublic: false,
        meta: {
            title: "Service Information",
        },
    },

    USER_PROFILE: {
        name: "user profile",
        path: "/userprofile",
        element: <UserProfileComponent />,
        isPublic: false,
        meta: {
            title: "User Profile",
        },
    },
};

const allRoutes = { ...publicRoutes, ...protectedRoutes };

/**
 * Converts a path with params into a function
 * e.g., "/org/:orgUuid/service/:serviceUuid/create" ->
 * (params) => `/org/${params.orgUuid}/service/${params.serviceUuid}/create`
 */
const createPathFunction = (path) => {
    const paramNames = (path?.match(/:[^/]+/g) || []).map((p) => p.substring(1));

    if (paramNames.length === 0) {
        return () => path;
    }

    return (params = {}) => {
        let result = path;
        paramNames.forEach((param) => {
            if (!params[param]) {
                console.warn(`Missing parameter "${param}" for path: ${path}`);
            }
            result = result.replace(`:${param}`, params[param] || `:${param}`);
        });
        return result;
    };
};

const enhanceRoutesWithPaths = (routeConfig) => {
    const enhanced = {};

    Object.entries(routeConfig).forEach(([key, route]) => {
        enhanced[key] = {
            ...route,
            pathFn: createPathFunction(route.path),
            path: route.path,
        };
    });

    return enhanced;
};

export const GlobalRoutes = enhanceRoutesWithPaths(allRoutes);
